import {
  CSSObject,
  List,
  ListItem,
  ListItemText,
  Theme,
  Box,
  Typography,
  Tooltip,
  useTheme,
  alpha,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import MuiDrawer from "@mui/material/Drawer";
import { styled } from "@mui/material/styles";
import { createContext, useContext } from "react";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import PaymentIcon from "@mui/icons-material/Payment";
import ListAltIcon from "@mui/icons-material/ListAlt";
import BeenhereIcon from "@mui/icons-material/Beenhere";
import SchoolIcon from "@mui/icons-material/School";
import EventIcon from "@mui/icons-material/Event";
import GroupIcon from "@mui/icons-material/Group";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Collapse } from "@mui/material";
import RememberMeIcon from "@mui/icons-material/RememberMe";
import MonitorIcon from "@mui/icons-material/Monitor";
import PeopleIcon from "@mui/icons-material/People";
import Icon from "../../assets/icon.png";

// Create a context to share sidebar state
export const SidebarContext = createContext({
  open: false,
  toggleSidebar: () => {},
  closeSidebar: () => {},
  drawerWidth: 230,
  isMobile: false,
});

export const useSidebar = () => useContext(SidebarContext);

const drawerWidth = 230;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1),
  minHeight: 64,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
}));

const StyledDrawer = styled(MuiDrawer)(({ theme, open, variant }) => {
  const isTemporary = variant === "temporary";
  const mixin = open ? openedMixin(theme) : closedMixin(theme);
  const paperSurface = {
    background: `linear-gradient(180deg, ${
      theme.palette.background.paper
    } 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
    boxShadow: `2px 0 8px ${alpha(theme.palette.common.black, 0.05)}`,
    backdropFilter: "blur(10px)",
  };

  return {
    ...(isTemporary ? {} : mixin),
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    zIndex: isTemporary ? theme.zIndex.modal : theme.zIndex.drawer,
    "& .MuiDrawer-paper": {
      ...(isTemporary ? openedMixin(theme) : mixin),
      ...paperSurface,
    },
  };
});

const StyledListItem = styled(ListItem)(({ theme }) => ({
  margin: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(1),
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",

  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      transform: "translateX(4px)",
      transition: "transform 0.3s ease",
    },
  },

  "&.active": {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}`,

    "& .sidebar-icon": {
      color: theme.palette.primary.main,
    },

    "& .MuiListItemText-primary": {
      color: theme.palette.primary.main,
      fontWeight: 700,
    },

    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 4,
      backgroundColor: theme.palette.primary.main,
      borderRadius: "0 2px 2px 0",
    },
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  padding: theme.spacing(1),

  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.1),
    transform: "scale(1.1)",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "1.5rem",
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flex: 1,

  "& img": {
    transition: "all 0.3s ease",
    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
  },

  "&:hover img": {
    transform: "scale(1.05)",
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  fontWeight: 700,
  fontSize: "1.2rem",
  letterSpacing: "0.5px",
  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
}));

interface SidebarProps {
  userType?: string | null;
}

const AdminSidebar = ({ userType }: SidebarProps) => {
  const theme = useTheme();
  const location = useLocation();
  const { open, toggleSidebar, closeSidebar, isMobile } = useSidebar();

  const allOptions = [
    {
      label: "Dashboard",
      path: "/admin/dashboard",
      icon: <DashboardIcon />,
    },
    {
      label: "Manage Lanes",
      path: "/admin/manage-lanes",
      icon: <ListAltIcon />,
    },
    {
      label: "Website Layout",
      path: "/admin/website-popup",
      icon: <MonitorIcon />,
    },
    {
      label: "Manage Bookings",
      path: "/admin/manage-bookings",
      icon: <BeenhereIcon />,
    },
    {
      label: "Memberships",
      path: "/admin/manage-memberships",
      icon: <RememberMeIcon />,
    },
    {
      label: "Payments",
      path: "/admin/payments",
      icon: <PaymentIcon />,
    },
    {
      label: "Enquiry",
      path: "/admin/enquiries",
      icon: <ContactSupportIcon />,
    },
    {
      label: "Users",
      path: "/admin/manage-users",
      icon: <PeopleIcon />,
    },
  ];

  // Filter options based on user type
  const sidebarOptions = (() => {
    if (userType === "employee" || userType === "employee_admin") {
      const labels = [
        "Website Layout",
        "Manage Bookings",
        "Memberships",
        "Payments",
        "Enquiry",
        "Users",
      ];
      if (userType === "employee_admin") {
        labels.push("Manage Lanes");
      }
      return allOptions.filter((opt) => labels.includes(opt.label));
    } else if (
      ["coach", "coach_employee", "coach_admin"].includes(userType || "")
    ) {
      // Coach roles only see Programs section
      return [];
    } else {
      // Admin sees all options
      return allOptions;
    }
  })();

  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const handleNavClick = () => {
    if (isMobile) {
      closeSidebar();
    }
  };

  return (
    <StyledDrawer
      variant={isMobile ? "temporary" : "permanent"}
      open={open}
      onClose={closeSidebar}
      ModalProps={{ keepMounted: true }}
    >
      <DrawerHeader>
        <LogoContainer>
          <img
            style={{
              height: "32px",
              width: "32px",
              borderRadius: "6px",
            }}
            src={Icon}
            alt="Logo"
          />
          {open && <LogoText variant="h6">The Bat Cage</LogoText>}
        </LogoContainer>
        <StyledIconButton onClick={toggleSidebar}>
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </StyledIconButton>
      </DrawerHeader>

      <Box
        sx={{
          flex: 1,
          // overflow: "auto",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          py: 1,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: alpha(theme.palette.primary.main, 0.3),
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: alpha(theme.palette.primary.main, 0.5),
          },
        }}
      >
        <List sx={{ px: 1 }}>
          {sidebarOptions.map((option) => {
            const active = isActive(option.path);
            return (
              <Tooltip
                key={option.label}
                title={!open && !isMobile ? option.label : ""}
                placement="right"
                arrow
              >
                <Link
                  key={option.label}
                  to={option.path}
                  onClick={handleNavClick}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <StyledListItem
                    className={active ? "active" : ""}
                    sx={{
                      cursor: "pointer",
                      px: 1.5,
                      py: 1.5,
                      minHeight: 48,
                      width: open ? "100%" : theme.spacing(8),
                    }}
                  >
                    <Box
                      className="sidebar-icon"
                      sx={{
                        color: active ? "primary.main" : "text.secondary",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 24,
                        width: 24,
                        height: 24,
                        mr: open ? 2 : 0,
                        flexShrink: 0,
                      }}
                    >
                      {!open && !isMobile ? (
                        <Tooltip
                          title={option.label}
                          placement="right"
                          arrow
                          disableInteractive
                        >
                          <Box component="span">{option.icon}</Box>
                        </Tooltip>
                      ) : (
                        option.icon
                      )}
                    </Box>
                    {open && (
                      <ListItemText
                        primary={option.label}
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight: active ? 700 : 500,
                          color: active ? "primary.main" : "text.primary",
                          noWrap: true,
                        }}
                      />
                    )}
                  </StyledListItem>
                </Link>
              </Tooltip>
            );
          })}

          {/* Programs Collapsible */}
          <SidebarProgramsGroup open={open} userType={userType} />
        </List>
      </Box>
    </StyledDrawer>
  );
};

export default AdminSidebar;

const SidebarProgramsGroup = ({
  open,
  userType,
}: {
  open: boolean;
  userType?: string | null;
}) => {
  const { isMobile, closeSidebar } = useSidebar();
  const location = useLocation();
  const [expanded, setExpanded] = useState(
    location.pathname.startsWith("/admin/manage-program")
  );
  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const items = [
    {
      label: "Manage Programs",
      path: "/admin/manage-programs",
      icon: <SchoolIcon />,
      roles: ["admin", "employee", "employee_admin", "coach_admin"],
    },
    {
      label: "Program Sessions",
      path: "/admin/manage-program-sessions",
      icon: <EventIcon />,
      roles: ["admin", "employee", "employee_admin", "coach_admin"],
    },
    {
      label: "Program Enrollments",
      path: "/admin/manage-program-enrollments",
      icon: <GroupIcon />,
      roles: ["admin", "employee", "employee_admin", "coach", "coach_employee", "coach_admin"],
    },
  ].filter((i) => {
    if (userType === "employee" || userType === "employee_admin") {
      return i.roles.includes(userType);
    } else if (
      ["coach", "coach_employee", "coach_admin"].includes(userType || "")
    ) {
      return i.roles.includes(userType || "");
    } else {
      return i.roles.includes("admin");
    }
  });

  const anyActive = items.some((i) => isActive(i.path));

  return (
    <>
      <StyledListItem
        className={anyActive ? "active" : ""}
        onClick={() => setExpanded(!expanded)}
        sx={{
          cursor: "pointer",
          px: 1.5,
          py: 1.5,
          minHeight: 48,
          width: open ? "100%" : "fit-content",
        }}
      >
        <Box
          className="sidebar-icon"
          sx={{
            color: anyActive ? "primary.main" : "text.secondary",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 24,
            width: 24,
            height: 24,
            mr: open ? 2 : 0,
          }}
        >
          <SchoolIcon />
        </Box>
        {open && (
          <ListItemText
            primary="Programs"
            primaryTypographyProps={{
              fontSize: "0.9rem",
              fontWeight: anyActive ? 700 : 500,
              color: anyActive ? "primary.main" : "text.primary",
              noWrap: true,
            }}
          />
        )}
        {open && (expanded ? <ExpandLess /> : <ExpandMore />)}
      </StyledListItem>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {items.map((option) => {
            const active = isActive(option.path);
            return (
              <Link
                key={option.path}
                to={option.path}
                onClick={() => {
                  if (isMobile) {
                    closeSidebar();
                  }
                }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <StyledListItem
                  className={active ? "active" : ""}
                  sx={{
                    cursor: "pointer",
                    px: 1.5,
                    py: 1.2,
                    minHeight: 40,
                    ml: open ? 2 : 0,
                    width: open ? "calc(100% - 16px)" : "fit-content",
                  }}
                >
                  <Box
                    className="sidebar-icon"
                    sx={{
                      color: active ? "primary.main" : "text.secondary",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 24,
                      width: 24,
                      height: 24,
                      mr: open ? 2 : 0,
                    }}
                  >
                    {option.icon}
                  </Box>
                  {open && (
                    <ListItemText
                      primary={option.label}
                      primaryTypographyProps={{
                        fontSize: "0.88rem",
                        fontWeight: active ? 700 : 500,
                        color: active ? "primary.main" : "text.primary",
                        noWrap: true,
                      }}
                    />
                  )}
                </StyledListItem>
              </Link>
            );
          })}
        </List>
      </Collapse>
    </>
  );
};
